import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe, Save } from "lucide-react";

const KEYS = [
  "dns_default_provider",
  "cloudflare_api_token",
  "cloudflare_zone_id",
  "cloudflare_shared_domain",
  "cloudflare_custom_hostname_target",
  "cloudflare_fallback_origin",
  "azure_tenant_id",
  "azure_client_id",
  "azure_client_secret",
  "azure_subscription_id",
  "azure_resource_group",
  "azure_app_service_name",
];

/**
 * DNS management settings — Cloudflare (live) + Azure App Service (origin
 * hostname binding) credentials, and Akamai fields reserved for phase 2.
 * Nothing here is hardcoded: every value is read/written through the same
 * generic `/settings/{key}` endpoints every other integration in this app
 * uses (see CmsIntegrationCard.jsx), gated to System Admin + MHQ Power Admin
 * only — the dealer/market DNS wizard roles never see this card.
 */
export function DnsIntegrationCard() {
  const [values, setValues] = useState(Object.fromEntries(KEYS.map((k) => [k, ""])));
  // Secrets are write-only: the API reports whether one is saved, never its value.
  const [secretSet, setSecretSet] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings");
        const map = Object.fromEntries(data.map((s) => [s.key, s.value]));
        setValues((prev) => Object.fromEntries(KEYS.map((k) => [k, map[k] ?? prev[k] ?? ""])));
        setSecretSet(Object.fromEntries(data.filter((s) => s.is_set).map((s) => [s.key, true])));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(KEYS.map((k) => client.put(`/settings/${k}`, { value: (values[k] || "").trim() })));
      toast.success("DNS management settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="settings-dns-card">
      <div className="mb-1 flex items-center gap-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">DNS Management</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Powers the self-service dealer DNS setup wizard — Cloudflare zone record + Custom Hostname credentials, and
        the Azure App Service origin the CMS runs on. Akamai fields are reserved for a future release.
      </p>

      <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Provider</div>
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Default DNS provider" k="dns_default_provider" values={values} set={set} loading={loading} saving={saving}
          placeholder="cloudflare" />
      </div>

      <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Cloudflare</div>
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="API token" k="cloudflare_api_token" values={values} set={set} loading={loading} saving={saving} mono type="password" secretSet={secretSet} />
        <Field label="Zone ID" k="cloudflare_zone_id" values={values} set={set} loading={loading} saving={saving} mono />
        <Field label="Shared market domain" k="cloudflare_shared_domain" values={values} set={set} loading={loading} saving={saving}
          placeholder="skodaauto.de" mono
          help="Base domain for the shared-subdomain path, e.g. dealer.skodaauto.de" />
        <Field label="Custom Hostname target (CNAME)" k="cloudflare_custom_hostname_target" values={values} set={set}
          loading={loading} saving={saving} mono
          help="The fixed CNAME target every independent-domain dealer points at — never changes on a provider swap" />
        <Field label="Fallback origin" k="cloudflare_fallback_origin" values={values} set={set} loading={loading} saving={saving} mono
          help="Custom Hostnames' fallback-origin setting — configured once in Cloudflare, separate from the target above" />
      </div>

      <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Azure App Service (origin)</div>
      <div className="mb-2 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Tenant ID" k="azure_tenant_id" values={values} set={set} loading={loading} saving={saving} mono />
        <Field label="Client ID" k="azure_client_id" values={values} set={set} loading={loading} saving={saving} mono />
        <Field label="Client secret" k="azure_client_secret" values={values} set={set} loading={loading} saving={saving} mono type="password" secretSet={secretSet} />
        <Field label="Subscription ID" k="azure_subscription_id" values={values} set={set} loading={loading} saving={saving} mono />
        <Field label="Resource group" k="azure_resource_group" values={values} set={set} loading={loading} saving={saving} mono />
        <Field label="App Service name" k="azure_app_service_name" values={values} set={set} loading={loading} saving={saving} mono />
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="settings-dns-save" onClick={save} disabled={loading || saving} className="gap-1.5">
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </div>
    </Card>
  );
}

function Field({ label, k, values, set, loading, saving, placeholder, help, mono, type = "text", secretSet }) {
  return (
    <div>
      <Label className="data-label mb-1 block">{label}</Label>
      <Input
        data-testid={`settings-dns-${k}`}
        type={type}
        placeholder={secretSet?.[k] && !values[k] ? "•••••••• saved — leave blank to keep" : placeholder}
        value={values[k]}
        onChange={set(k)}
        disabled={loading || saving}
        className={mono ? "font-mono text-xs" : ""}
        autoComplete={type === "password" ? "new-password" : "off"}
      />
      {help && <p className="mt-1 text-[10px] text-muted-foreground">{help}</p>}
    </div>
  );
}
